import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";

function ProjectTeamMemberList() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const orgId =
    userData?.organization?.organization_id ??
    userData?.user?.organization_id ??
    userData?.organization_id ??
    null;
  const [searchParams, setSearchParams] = useSearchParams();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const ViewFunction = (obj) => {
    navigate(`/project-team-member/view/${obj?.id}`);
  };

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const currentParams = new URLSearchParams(searchParams);
    let needsUpdate = false;

    if (!currentParams.has("page")) {
      currentParams.set("page", "1");
      needsUpdate = true;
    }
    if (!currentParams.has("pageSize")) {
      currentParams.set("pageSize", "10");
      needsUpdate = true;
    }
    if (!currentParams.has("sortBy")) {
      currentParams.set("sortBy", "organization_project_team_member_id");
      needsUpdate = true;
    }
    if (!currentParams.has("sortOrder")) {
      currentParams.set("sortOrder", "asc");
      needsUpdate = true;
    }

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true });
    }

    hasInitializedURL.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!orgId) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Project_team_members_grid_${orgId}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(null);
            }
          } else {
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [orgId]);

  const fetchTeamMembersWithFilters = useCallback(async () => {
    if (!orgId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${MAIN_URL}/api/project-management/project-team-members?mode=3&per_page=all&organization_id=${orgId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: fetchControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const rawData = result.data ?? result ?? [];
      const dataArray = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.data)
        ? rawData.data
        : [];

      const normalized = dataArray.map((item) => {
        const teamLabel =
          item.team ??
          item.project_team_name ??
          item.organization_project_team?.project_team_name ??
          (typeof item.organization_project_team_id === "string"
            ? item.organization_project_team_id
            : null) ??
          "-";
        const projectLabel =
          item.project ??
          item.project_name ??
          item.organization_project?.project_name ??
          (typeof item.organization_project_id === "string"
            ? item.organization_project_id
            : null) ??
          "-";
        const employeeLabel =
          item.employee ??
          item.full_name ??
          item.employee_name ??
          item.organization_person?.person?.full_name ??
          item.organization_person?.full_name ??
          (typeof item.organization_person_id === "string"
            ? item.organization_person_id
            : null) ??
          "-";
        const roleLabel =
          item.user_role ??
          item.user_role_name ??
          item.organization_user_role?.user_role_name ??
          (typeof item.organization_user_role_id === "string"
            ? item.organization_user_role_id
            : null) ??
          "-";

        return {
          ...item,
          id: item.organization_project_team_member_id ?? item.id,
          is_active:
            item.is_active === 1 ||
            item.is_active === true ||
            item.is_active === "1",
          is_team_lead:
            item.is_team_lead === 1 ||
            item.is_team_lead === true ||
            item.is_team_lead === "1",
          team: teamLabel,
          project: projectLabel,
          employee: employeeLabel,
          user_role: roleLabel,
          organization_project_team_id: teamLabel,
          organization_project_id: projectLabel,
          organization_person_id: employeeLabel,
          organization_user_role_id: roleLabel,
        };
      });

      // Client-side search
      const search = searchParams.get("search");
      let filtered = normalized;
      if (search && search.trim()) {
        const q = search.trim().toLowerCase();
        filtered = normalized.filter(
          (item) =>
            String(item.employee).toLowerCase().includes(q) ||
            String(item.team).toLowerCase().includes(q) ||
            String(item.project).toLowerCase().includes(q) ||
            String(item.user_role).toLowerCase().includes(q)
        );
      }

      // Client-side sort
      const sortBy =
        searchParams.get("sortBy") || "organization_project_team_member_id";
      const sortOrder = searchParams.get("sortOrder") || "asc";
      filtered = [...filtered].sort((a, b) => {
        const va = a[sortBy] ?? "";
        const vb = b[sortBy] ?? "";
        const cmp = String(va).localeCompare(String(vb), undefined, {
          numeric: true,
        });
        return sortOrder === "desc" ? -cmp : cmp;
      });

      // Client-side pagination
      const page = Number.parseInt(searchParams.get("page"), 10) || 1;
      const pageSize = Math.min(
        Number.parseInt(searchParams.get("pageSize"), 10) || 10,
        100
      );
      const total = filtered.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const start = (page - 1) * pageSize;
      const paged = filtered.slice(start, start + pageSize);

      setMembers(paged);
      setPaginationData({
        page,
        pageSize,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      });
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching team members:", err);
      toast.error("Failed to fetch team members");
      setMembers([]);
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, searchParams]);

  useEffect(() => {
    fetchTeamMembersWithFilters();
  }, [fetchTeamMembersWithFilters]);

  return (
    <>
      <Layout4
        heading="Project Team Members"
        Route="/project-team-member"
        btnName="Add Project Team Member"
        showAddButton={true}
        add_action="ADD_PROJECT_TEAM_MEMBER"
      />

      {loading || loadingConfig ? (
        <div style={{ padding: 16 }}>Loading...</div>
      ) : (
        <TableDataGeneric
          viewFunction={(obj) => ViewFunction(obj)}
          gridKey="Project_team_members_grid"
          tableName="organization_project_team_members"
          filterModule="organization_project_team_members"
          primaryKey="id"
          heading="organization_project_team_members"
          data={members}
          sortname={"organization_project_team_member_id"}
          showActions={true}
          Route="/project-team-member"
          token={localStorage.getItem("token")}
          configss={configColumns}
          edit_delete_action={[
            "EDIT_PROJECT_TEAM_MEMBER",
            "DELETE_PROJECT_TEAM_MEMBER",
          ]}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default ProjectTeamMemberList;
